import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { SEmptyState } from '../s-empty-state/SEmptyState';
import { SIndexTableCell, SIndexTableRow } from './components';
import SIndexTableBase, { SIndexTableBaseProps } from './components/SIndexTableBase';
import { SIndexTableProvider, SIndexTableProviderProps } from './index-provider';

export function SIndexTable(props: SIndexTableFactoryProps) {
    const { t } = useTranslation('polaris');

    const emptyStateMarkup = useMemo(
        () =>
            !!props?.itemCount ? undefined : !!props?.emptyState ? (
                <s-box padding="small-300">{props.emptyState}</s-box>
            ) : (
                <s-box padding="small-300">
                    <SEmptyState
                        image={'https://cdn.syncee.com/assets/images/empty_state/empty_find_products.png'}
                        heading={t('Polaris.IndexTable.emptySearchTitle', {
                            resourceNamePlural:
                                props.resourceName?.plural ?? t('Polaris.IndexProvider.defaultItemPlural'),
                        })}
                        description={t('Polaris.IndexTable.emptySearchDescription')}
                    />
                </s-box>
            ),
        [props.emptyState, props.resourceName, props.itemCount]
    );

    return (
        <SIndexTableProvider
            selectable={props.selectable && !props.condensed}
            disableSelection={props.disableSelection}
            itemCount={props.itemCount}
            selectedItemsCount={props.selectedItemsCount}
            resourceName={props.resourceName}
            loading={props.loading}
            hasMoreItems={props.hasMoreItems}
            condensed={props.condensed}
            onSelectionChange={props.onSelectionChange}
            paginatedSelectAllText={props.paginatedSelectAllText}
        >
            <SIndexTableBase
                headings={props.headings}
                pagination={props.pagination}
                emptyState={props.emptyState}
                loading={props.loading}
                condensed={props.condensed}
                resourceName={
                    props.resourceName ?? {
                        singular: t('Polaris.IndexProvider.defaultItemSingular'),
                        plural: t('Polaris.IndexProvider.defaultItemPlural'),
                    }
                }
                bulkActions={props.bulkActions}
                hideSelectAll={props.hideSelectAll}
            >
                {props.children}
            </SIndexTableBase>

            {emptyStateMarkup}
        </SIndexTableProvider>
    );
}

SIndexTable.Cell = SIndexTableCell;
SIndexTable.Row = SIndexTableRow;

export type SIndexTableFactoryProps = SIndexTableProviderProps & SIndexTableBaseProps;
